// @flow

import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { Helmet } from 'react-helmet';

import { titleTemplates } from 'data/global/metadata';
import type { ImageToWebPFragment } from 'graphql-types';
import { usePageState } from 'contexts';
import { PageType } from 'Constants';

export type PageMetaData = {
  title: string,
  description: string,
  noCrawl?: boolean,
  image?: ImageToWebPFragment,
};

type MetadataProps = {
  data: PageMetaData,
  location: Location,
};

const Metadata = (props: MetadataProps) => {
  const {
    data: { title, description, image, noCrawl },
    location: { origin, pathname },
  } = props;
  const { defaultImage } = useStaticQuery(graphql`
    query {
      defaultImage: file(relativeDirectory: { eq: "global/metadata" }, name: { eq: "default-share-image" }) {
        ...ImageToWebP
      }
    }
  `);
  const {
    page: { product },
  } = usePageState();
  const template = product.length === 1 ? titleTemplates[product[0]] : titleTemplates[PageType.PRODUCT.NONE];
  if (!template) throw new Error(`No title template set for ${product}`);
  if (!title) throw new Error(`No title set for ${pathname}`);
  if (!description) throw new Error(`No description set for ${pathname}`);
  const tags = [
    {
      name: 'description',
      content: description,
    },
    {
      name: 'og:type',
      content: 'website',
    },
    {
      name: 'og:url',
      content: `${origin}${pathname}`,
    },
    {
      name: 'og:title',
      content: title,
    },
    {
      name: 'og:description',
      content: description,
    },
    {
      name: 'og:image',
      content: image?.childImageSharp?.original?.src ?? defaultImage.childImageSharp.original.src,
    },
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:title`,
      content: title,
    },
    {
      name: `twitter:description`,
      content: description,
    },
  ];
  if (noCrawl) {
    tags.push({ name: 'robots', content: 'noindex, nofollow' });
  }
  return (
    <Helmet title={title} titleTemplate={`%s | ${template}`} meta={tags}>
      <html lang="en" />
    </Helmet>
  );
};

export default Metadata;
